import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useNormalizedMarketsStore } from '../state/normalizedMarkets.store';
import { useVisibleMarketGroupIds } from '../state/marketVisibility.store';
import type { MarketGroupType, MarketSubCategory } from '../services/interfaces/market.types';

import { useMarketPusher } from './useMarketPusher';
import { useGamePusher } from './useGamePusher';

type UseLoadedMarketsPusherOptions = {
  subCategory?: MarketSubCategory;
  types?: MarketGroupType[];
  enabled?: boolean;
};

type MarketGroupFilter = {
  subCategory?: MarketSubCategory;
  types?: MarketGroupType[];
};

const matchesSubCategory = (groupSubCategory: MarketSubCategory, filter: MarketGroupFilter) =>
  !filter.subCategory || groupSubCategory === filter.subCategory;

const matchesTypes = (groupType: MarketGroupType, filter: MarketGroupFilter) =>
  !filter.types?.length || filter.types.includes(groupType);

export function useLoadedMarketsPusher(options?: UseLoadedMarketsPusherOptions) {
  const { subCategory, types, enabled = true } = options ?? {};
  const typesKey = types?.join(',') ?? '';
  const visibleMarketGroupIds = useVisibleMarketGroupIds();

  const filter = useMemo<MarketGroupFilter>(
    () => ({ subCategory, types }),
    [subCategory, types]
  );

  const marketIds = useNormalizedMarketsStore(
    useShallow((state) =>
      Array.from(state.marketGroupsById.values())
        .filter(
          (group) =>
            visibleMarketGroupIds.has(group.id) &&
            matchesSubCategory(group.sub_category, filter) &&
            matchesTypes(group.type, filter)
        )
        .flatMap((group) => group.markets.map((market) => market.id))
    )
  );

  const eventIds = useNormalizedMarketsStore(
    useShallow((state) =>
      Array.from(
        new Set(
          Array.from(state.normalizedMarketGroupsById.values())
            .filter(
              (group) =>
                !!group.eventId &&
                visibleMarketGroupIds.has(group.id) &&
                matchesSubCategory(group.subCategory, filter) &&
                matchesTypes('gameline', filter)
            )
            .map((group) => group.eventId)
        )
      )
    )
  );

  const source = useMemo(
    () => (subCategory ? `LoadedMarkets-${subCategory}-${typesKey || 'all'}` : 'LoadedMarkets'),
    [subCategory, typesKey]
  );

  useMarketPusher({ marketIds, source, enabled });
  useGamePusher({ eventIds, source, enabled });

  return {
    marketCount: marketIds.length,
    eventCount: eventIds.length,
  };
}
